/**
 * Kopfzeile einer Content-Feld-Karte (Variant D, 3-Spalten).
 *
 * Links: Label + „nur …"-Restriktions-Badge. Mitte: Abweichung/Vom Master/Leer.
 * Rechts: Zeichenzähler · Versions-Button · KI-Text (AIFieldButton).
 */

import { useTranslation } from 'react-i18next'
import { History, GitCompareArrows, Link2, TriangleAlert } from 'lucide-react'
import { AIFieldButton } from '@/components/v2/products/ai-field'
import { EID } from '@/lib/element-ids.registry'

interface Props {
  label: string
  restrictionNote?: string
  deviation: 'override' | 'inherited' | null
  counterLen: number
  max?: number
  blockType: string
  versionsOpen: boolean
  onToggleVersions: () => void
  productId: string
  channelId?: string
  /** false = Versions-Button ausblenden (z.B. noch nicht angelegter Block). Default true. */
  canShowVersions?: boolean
}

export function FieldHeader({ label, restrictionNote, deviation, counterLen, max, blockType, versionsOpen, onToggleVersions, productId, channelId, canShowVersions = true }: Props) {
  const { t } = useTranslation()
  const over = max != null && counterLen > max

  return (
    <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 mb-1.5">
      <div className="flex items-center gap-2 min-w-0">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">{label}</h4>
        <span className={`content-field-counter text-xs tabular-nums whitespace-nowrap ${over ? 'text-red-600 dark:text-red-400' : 'text-neutral-400'}`}>
          {t('productContent.channels.charsCounter', { value: max != null ? `${counterLen} / ${max}` : `${counterLen}` })}
        </span>
        {restrictionNote && (
          <span className="content-field-only flex items-center gap-1 text-2xs font-mono font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
            <TriangleAlert className="h-2.5 w-2.5" aria-hidden="true" />{t('productContent.channels.onlyNote', { note: restrictionNote })}
          </span>
        )}
      </div>
      <div className="flex justify-center">
        {deviation === 'override' && (
          <span className="content-field-deviation flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400">
            <GitCompareArrows className="h-3.5 w-3.5" aria-hidden="true" />{t('productContent.channels.overriddenLabel')}
          </span>
        )}
        {deviation === 'inherited' && (
          <span className="content-field-inherited flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
            <Link2 className="h-3.5 w-3.5" aria-hidden="true" />{t('productContent.channels.inheritedFromMaster')}
          </span>
        )}
      </div>
      <div className="flex items-center justify-end gap-2">
        {canShowVersions && (
          <button
            type="button"
            id={`${EID['content-field-versions-toggle']}-${blockType}`}
            className="content-field-versions-toggle flex items-center gap-1 text-xs text-neutral-600 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400"
            onClick={onToggleVersions}
            aria-expanded={versionsOpen}
          >
            <History className="h-3.5 w-3.5" />{t('productContent.channels.versions.title')}
          </button>
        )}
        <AIFieldButton productId={productId} fieldType={blockType} channelId={channelId} />
      </div>
    </div>
  )
}
